import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, X } from 'lucide-react';

// User uploaded videos
const userVideos = [
  {
    id: 1,
    src: 'https://customer-assets.emergentagent.com/job_mel-salon-luxury/artifacts/14gid1ny_6c9c273d-0aaa-4ebd-9544-e7100510bbb2.mp4',
    title: 'Signature Style'
  },
  {
    id: 2,
    src: 'https://customer-assets.emergentagent.com/job_mel-salon-luxury/artifacts/75idfuq0_8d615dd6-d352-49f1-91eb-08ce5dca24a0.mp4',
    title: 'Latest Creation'
  },
  {
    id: 3,
    src: 'https://customer-assets.emergentagent.com/job_mel-salon-luxury/artifacts/320t38pp_89d36ab1-5880-4995-aa38-628db155ada3.mp4',
    title: 'Braiding Excellence'
  },
  {
    id: 4,
    src: 'https://customer-assets.emergentagent.com/job_mel-salon-luxury/artifacts/va5ligy0_503c6750-6fe9-4faa-8b29-ee3b6e08e048.mp4',
    title: 'Client Transformation'
  },
  {
    id: 5,
    src: 'https://customer-assets.emergentagent.com/job_mel-salon-luxury/artifacts/dtojd7af_616f275d-7d1c-4c80-905a-b46558e82034.mp4',
    title: 'Style Showcase'
  }
];

export const Gallery = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);

  return (
    <div className="page-transition pt-24">
      {/* Hero */}
      <section className="py-20 px-6 bg-[#050505]">
        <div className="max-w-7xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-script text-5xl md:text-7xl text-gold mb-4"
          >
            Our Gallery
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-sans text-[#A3A3A3] text-lg max-w-2xl mx-auto"
          >
            Witness the artistry that has made us the choice of A-list celebrities
          </motion.p>
        </div>
      </section>

      {/* Video Gallery Grid */}
      <section className="py-16 px-6 bg-surface">
        <div className="max-w-7xl mx-auto">
          {/* Featured Video - First video larger */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <div
              className="gallery-video relative aspect-video rounded-2xl overflow-hidden border border-gold/30 cursor-pointer group"
              onClick={() => setSelectedVideo(userVideos[0])}
              data-testid="featured-video"
            >
              <video
                src={userVideos[0].src}
                className="w-full h-full object-cover"
                loop
                muted
                playsInline
                autoPlay
                preload="auto"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="w-20 h-20 rounded-full bg-pink/80 flex items-center justify-center">
                  <Play className="w-10 h-10 text-white ml-1" />
                </div>
              </div>
              <div className="absolute bottom-6 left-6">
                <span className="font-script text-3xl text-gold text-shadow">{userVideos[0].title}</span>
              </div>
            </div>
          </motion.div>

          {/* Grid of remaining videos */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {userVideos.slice(1).map((video, index) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="gallery-video relative aspect-[9/16] rounded-xl overflow-hidden border border-gold/20 cursor-pointer group"
                onClick={() => setSelectedVideo(video)}
                data-testid={`gallery-video-${video.id}`}
              >
              <video
                  src={video.src}
                  className="w-full h-full object-cover"
                  loop
                  muted
                  playsInline
                  autoPlay
                  preload="auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-14 h-14 rounded-full bg-pink/80 flex items-center justify-center">
                    <Play className="w-7 h-7 text-white ml-0.5" />
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <span className="font-script text-xl text-gold text-shadow">{video.title}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Social CTA */}
      <section className="py-24 px-6 bg-[#050505]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-script text-4xl md:text-5xl text-gold mb-6"
          >
            Follow Our Journey
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-sans text-[#A3A3A3] text-lg mb-10"
          >
            Stay updated with our latest styles and transformations
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="flex justify-center gap-6"
          >
            <a
              href="https://facebook.com/share/18TvPyzdbo/?mibextid=wwXIfr"
              target="_blank"
              rel="noopener noreferrer"
              className="w-14 h-14 rounded-full border border-gold flex items-center justify-center text-gold hover:bg-pink hover:border-pink hover:text-white transition-all"
              data-testid="gallery-facebook"
            >
              <i className="fab fa-facebook-f text-xl"></i>
            </a>
            <a
              href="https://tiktok.com/@braidsbymel25"
              target="_blank"
              rel="noopener noreferrer"
              className="w-14 h-14 rounded-full border border-gold flex items-center justify-center text-gold hover:bg-pink hover:border-pink hover:text-white transition-all"
              data-testid="gallery-tiktok"
            >
              <i className="fab fa-tiktok text-xl"></i>
            </a>
            <a
              href="https://www.instagram.com/reel/DVghGgEE37e/?igsh=MTAxNzgxaDRmMTh4dQ=="
              target="_blank"
              rel="noopener noreferrer"
              className="w-14 h-14 rounded-full border border-gold flex items-center justify-center text-gold hover:bg-pink hover:border-pink hover:text-white transition-all"
              data-testid="gallery-instagram"
            >
              <i className="fab fa-instagram text-xl"></i>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Video Modal */}
      <AnimatePresence>
        {selectedVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={() => setSelectedVideo(null)}
          >
            <button
              className="absolute top-6 right-6 text-gold hover:text-pink transition-colors"
              data-testid="close-video-modal"
            >
              <X size={32} />
            </button>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="max-w-4xl w-full aspect-[9/16] md:aspect-video"
              onClick={(e) => e.stopPropagation()}
            >
              <video
                src={selectedVideo.src}
                className="w-full h-full object-contain rounded-xl"
                controls
                autoPlay
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
